import { useEffect, useMemo, useRef } from 'react';

/**
 * Reports a panel's payload to the host once after mount and again whenever its content changes. The trigger is the
 * content, not object identity or the callback's: a host that re-renders the panel, or hands it a new callback or an equal
 * template each time, gets no second report, so it can't loop through here. The ref holds the last key reported, which
 * also keeps a StrictMode remount to one call. An effect reports, so nothing goes out after the panel is gone.
 * Memoize the payload on its inputs: the key is built from it only when it changes.
 * @param {object} payload - what Save would submit, built without validation
 * @param {((data: object) => void)|undefined} onValuesChange
 */
export function useReportValues(payload, onValuesChange) {
  const key = useMemo(() => JSON.stringify(payload), [payload]);
  const reportedKey = useRef(null);
  useEffect(() => {
    if (!onValuesChange || reportedKey.current === key) return;
    reportedKey.current = key;
    onValuesChange(payload);
  }, [onValuesChange, payload, key]);
}
